import { useMemo, Fragment, useState } from "react";
import { DatesProvider } from "dates/DatesContext";
import { useDates } from "dates/DatesContext";
import { useModuleEnabled } from "redux/selectors/modules";
import { useParams, Redirect } from "react-router-dom";
import { Col, Row, Table as ReadOnlyTable } from "react-bootstrap";
import useDSPCampaignData from "./useDSPCampaignData";
import useDSPCreativeData from "./useDSPCreativeData";
import BreadcrumbDates from "components/core/blocks/BreadcrumbDates";
import { DASHBOARD_LINKS } from "utils/dashboardLinks";
import MetricsCard from "components/custom/merchandise/MetricsCard";
import { buildDSPMetric } from "./metrics";
import useDSPLineItemData from "./useDSPLineItemData";
import { snakeCaseToSentenceCase, toTitleCase } from "utils/formatText";
import moment from "moment";
import { Button } from "components/core/basic/Button";
import DetailedMetric from "components/core/blocks/DetailedMetric";
import { formatCurrency } from "utils/formatNumber";
import Loading from "components/core/blocks/Loading";
import DSPCreativesTable from "./DSPCreativesTable";
import { useAppSelector } from "redux/hooks";
import DSPAudiencesTable from "./DSPAudiencesTable";
import DSPInventoryTable from "./DSPInventoryTable";
import DSPOverlapDashboard from "./DSPOverlapDashboad";
import DSPLineItemForm from "./forms/DSPLineItemForm";
import { useQueryClient } from "@tanstack/react-query";

import CenteredModal from "components/core/basic/CenteredModal";
import useQueryParams from "hooks/useQueryParams";
import DSPLineItemAutomation from "./DSPLineItemAutomation";
import Tabs from "components/core/basic/Tabs";
import LineItemBiddingProgramLog from "components/custom/programs/bidding/LineItemBiddingProgramLog";
import DSPProductsTable from "./DSPProductsTable";
import DSPGeoDashboard from "./DSPGeoDashboard";
import DSPAudienceGroupsForm from "./forms/DSPAudienceGroupsForm";
import { useDSPLineItemCreativeAssociationData } from "./hooks/useDSPLineItemCreativeAssociationData";
import { Flex } from "../../../styled-system/jsx";
import ConfirmationModal from "components/core/blocks/ConfirmationModal";
import Switch from "react-bootstrap-switch";
import useDSPSellerInfo from "./hooks/useDSPSellerInfo";

const DSP_TABS = [
  {
    key: "settings",
    label: "Settings",
    children: [
      { key: "lineItemSettings", label: "Summary" },
      { key: "automation", label: "Automation" },
      { key: "bidding_logs", label: "Bidding Logs" },
      { key: "target_audiences", label: "Target Audiences" },
    ],
  },
  { key: "audiences", label: "Audiences" },
  { key: "audienceOpportunities", label: "Audience Overlap" },
  { key: "creatives", label: "Creatives" },
  { key: "inventory", label: "Inventory" },
  { key: "products", label: "Products" },
  { key: "featured_products", label: "Featured Products" },
  { key: "geo", label: "Geography" },
];
const DEFAULT_TAB = DSP_TABS[0].children[0].key;

const Dashboard = () => {
  const [selectedTab, setSelectedTab] = useQueryParams("tab", DEFAULT_TAB);
  const [showLineItemEditModal, setShowLineItemEditModal] = useState(false);
  const [cloneLineItem, setCloneLineItem] = useState(false);
  const [cloneWithCreatives, setCloneWithCreatives] = useState(true);

  const marketPlace = useAppSelector((state) => state.marketPlace);
  const channel = useAppSelector((state) => state.channel);
  const hasDSPModule = useModuleEnabled("DSP");
  const { id: lineItemId } = useParams();
  const { group, start, end } = useDates();
  const queryClient = useQueryClient();

  const { data: dspTimeseriesData } = useDSPLineItemData(`line_item_id,${group}`, true, true, {
    line_item_id: lineItemId,
  });

  const { dspAdvertiser } = useDSPSellerInfo();

  const {
    data: lineItemData,
    metadata: lineItemMetadata,
    targetData,
    isLoading,
  } = useDSPLineItemData("line_item_id", true, true, { line_item_id: lineItemId }, true);

  const { data: creativeAssociationData, isLoading: isCreativeAssocationDataLoading } =
    useDSPLineItemCreativeAssociationData(lineItemId);

  const lineItemDetails = useMemo(() => {
    return { ...lineItemMetadata?.[0], targets: targetData ?? [], creativeAssociations: creativeAssociationData };
  }, [lineItemMetadata, targetData, creativeAssociationData]);

  const { metadata: allLineItems } = useDSPLineItemData("line_item_id", true, false, {});

  const { metadata: dspCampaignMetadata, isLoading: isDSPCampaignDataLoading } = useDSPCampaignData(
    "campaign_id",
    true,
    false,
    {},
  );

  const {
    data: dspCreativeStatsData,
    metadata: dspCreativeMetadata,
    isLoading: isDSPCreativeDataLoading,
  } = useDSPCreativeData("creative_id", true, true, { line_item_id: lineItemId });

  const lineItemBreadcrumbOptions = useMemo(() => {
    return allLineItems?.filter((lineItem) => lineItem.campaign_id === lineItemMetadata?.[0]?.campaign_id);
  }, [lineItemMetadata, allLineItems]);

  const lineItemOverviewData = useMemo(() => {
    return {
      total: lineItemData?.[0] ?? {},
      date:
        dspTimeseriesData?.map((row) => {
          return { ...row, start_date: row.date };
        }) ?? [],
      metadata: lineItemMetadata ?? [],
      isLoading: isDSPCampaignDataLoading,
    };
  }, [lineItemMetadata, lineItemData, dspTimeseriesData, isDSPCampaignDataLoading]);

  const secondaryMetricTileData = useMemo(() => {
    if (!lineItemOverviewData.metadata?.length || !dspCampaignMetadata?.length) {
      return {};
    }

    const metadata = lineItemOverviewData?.metadata?.[0] ?? {};

    const orderMetadata = dspCampaignMetadata?.find(
      (order) => order.campaign_id === lineItemMetadata?.[0]?.campaign_id,
    );

    const processedData = {
      state: snakeCaseToSentenceCase(metadata.state ?? ""),
      deliveryStatus: snakeCaseToSentenceCase(metadata.delivery_status ?? ""),
      orderStartDate: moment.min(orderMetadata.flights?.map((f) => moment(f.startDateTime)))?.format("MMMM DD, YYYY"),
      orderEndDate: moment
        .max(orderMetadata.flights?.map((f) => moment(f.endDateTime)))
        .add(1, "day")
        ?.format("MMMM DD, YYYY"),
      lineItemStartDate: moment(metadata.start_date_time)?.format("MMMM D, YYYY"),
      lineItemEndDate: moment(metadata.end_date_time)?.format("MMMM D, YYYY"),
      budget: metadata.budget_amount,
      optimization: snakeCaseToSentenceCase(metadata.optimization?.bidStrategy ?? "")?.replace("Kpi", "KPI"),
      durationInDays: moment(metadata.end_date_time).diff(moment(metadata.start_date_time), "days"),
      daysCompleted: moment()?.diff(moment(metadata.start_date_time), "days"),
    };

    return processedData;
  }, [lineItemOverviewData, lineItemMetadata, dspCampaignMetadata]);

  const dspCreativesData = useMemo(() => {
    const combinedData = dspCreativeStatsData?.map((stats) => {
      const metadata = dspCreativeMetadata?.find((row) => row.creative_id === stats.creative_id) ?? {};

      return { ...metadata, ...stats };
    });
    return combinedData;
  }, [dspCreativeMetadata, dspCreativeStatsData]);

  const lineItemName = lineItemMetadata?.[0]?.name;
  const campaignName = dspCampaignMetadata?.find(
    (order) => order.campaign_id === lineItemMetadata?.[0]?.campaign_id,
  )?.name;
  const advertiserId = lineItemMetadata?.[0].advertiser_id;
  const orderId = lineItemMetadata?.[0].campaign_id;

  // Overview stat tile and chart metrics
  const overviewMetrics = useMemo(() => {
    return [
      buildDSPMetric(marketPlace.marketPlace, lineItemOverviewData, "dsp_sales", "dsp_sales_percent"),
      buildDSPMetric(marketPlace.marketPlace, lineItemOverviewData, "dsp_total_sales", "dsp_total_sales_percent"),
      buildDSPMetric(marketPlace.marketPlace, lineItemOverviewData, "dsp_ecpm", "dsp_ecpm_percent"),
      buildDSPMetric(marketPlace.marketPlace, lineItemOverviewData, "dsp_impressions", "dsp_impressions_percent"),
      buildDSPMetric(
        marketPlace.marketPlace,
        lineItemOverviewData,
        "dsp_viewable_impressions",
        "dsp_viewable_impressions_percent",
      ),
      buildDSPMetric(marketPlace.marketPlace, lineItemOverviewData, "dsp_viewability", "dsp_viewability_delta"),
      buildDSPMetric(marketPlace.marketPlace, lineItemOverviewData, "dsp_clicks", "dsp_clicks_percent"),
      buildDSPMetric(marketPlace.marketPlace, lineItemOverviewData, "dsp_cost", "dsp_cost_percent"),
      buildDSPMetric(
        marketPlace.marketPlace,
        lineItemOverviewData,
        "dsp_total_purchases",
        "dsp_total_purchases_percent",
      ),
      buildDSPMetric(marketPlace.marketPlace, lineItemOverviewData, "dsp_units", "dsp_units_percent"),
      buildDSPMetric(marketPlace.marketPlace, lineItemOverviewData, "dsp_total_units", "dsp_total_units_percent"),
      buildDSPMetric(marketPlace.marketPlace, lineItemOverviewData, "dsp_ctr", "dsp_ctr_delta"),
      buildDSPMetric(marketPlace.marketPlace, lineItemOverviewData, "dsp_total_acos", "dsp_total_acos_delta"),
      buildDSPMetric(marketPlace.marketPlace, lineItemOverviewData, "dsp_total_roas", "dsp_total_roas_delta"),
      buildDSPMetric(
        marketPlace.marketPlace,
        lineItemOverviewData,
        "dsp_total_detail_page_views",
        "dsp_total_detail_page_views_percent",
      ),
      buildDSPMetric(
        marketPlace.marketPlace,
        lineItemOverviewData,
        "dsp_total_detail_page_view_rate",
        "dsp_total_detail_page_view_rate_delta",
      ),
      buildDSPMetric(
        marketPlace.marketPlace,
        lineItemOverviewData,
        "dsp_total_add_to_cart",
        "dsp_total_add_to_cart_percent",
      ),
      buildDSPMetric(
        marketPlace.marketPlace,
        lineItemOverviewData,
        "dsp_new_to_brand_detail_page_views",
        "dsp_new_to_brand_detail_page_views_percent",
      ),
      buildDSPMetric(
        marketPlace.marketPlace,
        lineItemOverviewData,
        "dsp_new_to_brand_detail_page_views_rate",
        "dsp_new_to_brand_detail_page_views_rate_delta",
      ),
      buildDSPMetric(
        marketPlace.marketPlace,
        lineItemOverviewData,
        "dsp_total_new_to_brand_dpvs",
        "dsp_total_new_to_brand_dpvs_percent",
      ),
      buildDSPMetric(
        marketPlace.marketPlace,
        lineItemOverviewData,
        "dsp_total_new_to_brand_dpv_rate",
        "dsp_total_new_to_brand_dpv_rate_delta",
      ),
      buildDSPMetric(marketPlace.marketPlace, lineItemOverviewData, "dsp_brand_searches", "dsp_brand_searches_percent"),
      buildDSPMetric(
        marketPlace.marketPlace,
        lineItemOverviewData,
        "dsp_brand_searches_rate",
        "dsp_brand_searches_rate_delta",
      ),
      buildDSPMetric(marketPlace.marketPlace, lineItemOverviewData, "dsp_avg_base_bid", "dsp_avg_base_bid_percent"),
    ];
  }, [lineItemOverviewData, marketPlace]);

  const breadCrumbItems = useMemo(() => {
    return [
      {
        name: "DSP Dashboard",
        href: `/user/dsp/${dspAdvertiser?.id}/dashboard`,
        unsorted: true,
        dropdownLinkOptions: DASHBOARD_LINKS,
        dropdownSubtitle: "Dashboards",
        activeOption: "DSP Dashboard",
      },
      {
        name: campaignName,
        href: `/user/dsp/${dspAdvertiser?.id}/orders/${lineItemMetadata?.[0]?.campaign_id}`,
        dropdownLinkOptions: dspCampaignMetadata,
        searchable: true,
        searchPlaceholderText: "Search DSP orders...",
        dropdownSubtitle: "DSP Orders",
        activeOption: campaignName,
      },
      {
        name: lineItemName,
        href: `/user/dsp/${dspAdvertiser?.id}/lineitems/${lineItemId}`,
        dropdownLinkOptions: lineItemBreadcrumbOptions,
        searchable: true,
        searchPlaceholderText: "Search DSP line items...",
        dropdownSubtitle: "DSP Line Items",
      },
    ];
  }, [
    dspAdvertiser,
    lineItemId,
    lineItemName,
    lineItemMetadata,
    campaignName,
    dspCampaignMetadata,
    lineItemBreadcrumbOptions,
  ]);

  const orderDates = useMemo(() => {
    const orderMetadata = dspCampaignMetadata?.find(
      (order) => order.campaign_id === lineItemMetadata?.[0]?.campaign_id,
    );

    return {
      startDate: moment.min(orderMetadata?.flights?.map((f) => moment(f.startDateTime)))?.toISOString(),
      endDate: moment.max(orderMetadata?.flights?.map((f) => moment(f.endDateTime)))?.toISOString(),
    };
  }, [dspCampaignMetadata, lineItemMetadata]);

  const closeModal = () => {
    setShowLineItemEditModal(false);
    setCloneLineItem(false);
  };

  const renderTabContent = (tab) => {
    switch (tab) {
      case "lineItemSettings": {
        const { optimization, creation_date_time, name, state, frequencies, delivery_status, targeting_settings, bid } =
          lineItemMetadata?.[0] ?? {};
        return (
          <>
            <Col xs={12} className="d-flex justify-content-end align-items center mx-auto">
              <CenteredModal
                adPlanModal={true}
                show={showLineItemEditModal}
                closeModal={() => {
                  closeModal();
                }}
                content={
                  <Row>
                    <Col xs={12}>
                      <DSPLineItemForm
                        isLoading={isLoading || isCreativeAssocationDataLoading}
                        initialFormData={
                          cloneLineItem ?
                            {
                              ...lineItemDetails,
                              inventory_type: "DISPLAY",
                              id: null,
                              name: `${lineItemDetails.name} (copy)`,
                              creativeAssociations: cloneWithCreatives ? lineItemDetails.creativeAssociations : [],
                              start_date_time:
                                moment(orderDates?.startDate).isAfter(moment()) ?
                                  moment(orderDates?.startDate).toISOString()
                                : moment().startOf("hour").add(1, "hour").toISOString(),
                            }
                          : (lineItemDetails ?? {})
                        }
                        onCancel={() => {
                          closeModal();
                        }}
                        onSuccessfulSubmit={() => {
                          queryClient.invalidateQueries({
                            queryKey: ["dspLineItemData"],
                          });
                          closeModal();
                        }}
                        cloneMode={cloneLineItem}
                        orderId={orderId}
                        campaignId={lineItemMetadata?.[0]?.campaign_id}
                        adGroupId={lineItemId}
                        advertiserId={advertiserId}
                        orderStartDate={orderDates?.startDate}
                        orderEndDate={orderDates?.endDate}
                      />
                    </Col>
                  </Row>
                }
              />
              <Flex gap="medium">
                <Button
                  onPress={() => {
                    setShowLineItemEditModal(true);
                  }}
                >
                  Edit Line Item
                </Button>
                <ConfirmationModal
                  title={"Clone Line Item Details"}
                  modalButtonText={"Clone Line Item"}
                  confirmButtonText={"Continue"}
                  confirmationMessage={
                    <Flex direction="column" alignItems={"center"}>
                      <p>Would you like to use the same creatives from the source line item?</p>
                      <Flex gap={"medium"} alignItems={"center"} padding={"medium"}>
                        <span>No</span>
                        <Switch
                          onText=""
                          offText=""
                          value={cloneWithCreatives}
                          onColor="#FFFFFF"
                          onChange={() => {
                            setCloneWithCreatives(!cloneWithCreatives);
                          }}
                        />
                        <span>Yes</span>
                      </Flex>
                    </Flex>
                  }
                  onConfirm={() => {
                    setCloneLineItem(true);
                    setShowLineItemEditModal(true);
                  }}
                />
              </Flex>
            </Col>
            <Col xs={12} lg={6}>
              <ReadOnlyTable borderless hover>
                <tbody>
                  <tr>
                    <th>Name</th>
                    <td>{name}</td>
                  </tr>
                  <tr>
                    <th>State</th>
                    <td>{toTitleCase(state ?? "")}</td>
                  </tr>

                  <tr>
                    <th>Delivery Status</th>
                    <td>{toTitleCase(snakeCaseToSentenceCase(delivery_status ?? ""))}</td>
                  </tr>
                  <tr>
                    <th>Created On</th>
                    <td>{moment(creation_date_time).format("MMMM DD, YYYY")}</td>
                  </tr>
                  <tr>
                    <th>Optimization Strategy</th>
                    <td>{snakeCaseToSentenceCase(optimization?.bidStrategy ?? "")}</td>
                  </tr>
                  <tr>
                    <th>Base Bid</th>
                    <td>{formatCurrency(bid?.baseBid, marketPlace.marketPlace)}</td>
                  </tr>
                  <tr>
                    <th>Maximum Average CPM</th>
                    <td>
                      {bid?.maxAverageCPM ?
                        formatCurrency(bid?.maxAverageCPM, marketPlace.marketPlace)
                      : "Automatically Optimize"}
                    </td>
                  </tr>
                  {targeting_settings?.userLocation && (
                    <tr>
                      <th>Targeting User Location</th>
                      <td>{toTitleCase(targeting_settings?.userLocation)}</td>
                    </tr>
                  )}
                  {targeting_settings?.userLocationSignal && (
                    <tr>
                      <th>User Location Signals</th>
                      <td>{snakeCaseToSentenceCase(targeting_settings?.userLocationSignal)}</td>
                    </tr>
                  )}
                  {targeting_settings?.amazonViewability?.viewabilityTier && (
                    <tr>
                      <th>Amazon Viewability</th>
                      <td>{snakeCaseToSentenceCase(targeting_settings?.amazonViewability?.viewabilityTier)}</td>
                    </tr>
                  )}
                  {targeting_settings?.timeZoneType && (
                    <tr>
                      <th>Time Zone Type</th>
                      <td>{toTitleCase(targeting_settings?.timeZoneType)}</td>
                    </tr>
                  )}
                  <tr>
                    <th>Frequencies</th>
                    <td>
                      {frequencies?.map((frequency, index) => (
                        <Fragment key={index}>
                          <span>{toTitleCase(frequency.frequencyType)}</span>
                          {frequency.maxImpressions && (
                            <span>: no more than {frequency.maxImpressions} impressions a day</span>
                          )}
                        </Fragment>
                      ))}
                    </td>
                  </tr>
                </tbody>
              </ReadOnlyTable>
            </Col>
          </>
        );
      }
      case "configuration": {
        return (
          <Col xs={12}>
            <Row>
              <DSPLineItemForm
                isLoading={isLoading || isCreativeAssocationDataLoading}
                initialFormData={lineItemDetails ?? {}}
                onSuccessfulSubmit={() => {
                  queryClient.invalidateQueries({
                    queryKey: ["dspLineItemData"],
                  });
                }}
                orderId={orderId}
                adGroupId={lineItemId}
                advertiserId={advertiserId}
                orderStartDate={orderDates?.startDate}
                orderEndDate={orderDates?.endDate}
              />
            </Row>
          </Col>
        );
      }
      case "audiences": {
        return (
          <Col xs={12}>
            <Row>
              <DSPAudiencesTable adGroupId={lineItemId} totalImpressions={lineItemOverviewData?.total?.impressions} />
            </Row>
          </Col>
        );
      }
      case "audienceOpportunities": {
        return (
          <Col xs={12}>
            <Row>
              <DSPOverlapDashboard lineItemId={lineItemId} />
              <DSPAudiencesTable
                adGroupId={lineItemId}
                untargetedReport={true}
                totalImpressions={lineItemOverviewData?.total?.impressions}
              />
            </Row>
          </Col>
        );
      }
      case "creatives": {
        return (
          <Col xs={12}>
            <Row>
              <DSPCreativesTable
                data={dspCreativesData}
                isLoading={isDSPCreativeDataLoading}
                expanderFilters={{ line_item_id: lineItemId }}
              />
            </Row>
          </Col>
        );
      }
      case "inventory": {
        return (
          <Col xs={12}>
            <Row>
              <DSPInventoryTable adGroupId={lineItemId} />
            </Row>
          </Col>
        );
      }
      case "products": {
        return (
          <Col xs={12}>
            <Row>
              <DSPProductsTable adGroupId={lineItemId} />
            </Row>
          </Col>
        );
      }
      case "featured_products": {
        return (
          <Col xs={12}>
            <Row>
              <DSPProductsTable adGroupId={lineItemId} featuredAsinsFilter={true} />
            </Row>
          </Col>
        );
      }
      case "automation": {
        return (
          <Col xs={12}>
            <Row>
              <DSPLineItemAutomation lineItemId={lineItemMetadata?.[0]?.id} />
            </Row>
          </Col>
        );
      }
      case "bidding_logs": {
        return (
          <Col xs={12}>
            <Row>
              <LineItemBiddingProgramLog lineItemId={lineItemMetadata?.[0]?.id} />
            </Row>
          </Col>
        );
      }
      case "geo": {
        return (
          <Col xs={12}>
            <Row>
              <DSPGeoDashboard lineItemId={lineItemId} />
            </Row>
          </Col>
        );
      }
      case "target_audiences": {
        return (
          <Col xs={12}>
            <Row>
              <DSPAudienceGroupsForm lineItemId={lineItemMetadata?.[0]?.id} advertiserId={advertiserId} />
            </Row>
          </Col>
        );
      }
      default: {
        return null;
      }
    }
  };

  if (!hasDSPModule || channel.currentChannel !== "amazon") {
    return <Redirect to="/user/dashboard" />;
  }

  return (
    <div style={{ minHeight: "calc(100vh - 150px" }}>
      <Row>
        <Col xs={12} className="mb-4">
          <BreadcrumbDates items={breadCrumbItems} />
        </Col>
      </Row>
      <Row className="pb-5">
        <Col xs={12} className="px-5">
          <MetricsCard
            id="dspOverviewDashboard"
            title={lineItemName}
            initialSelectedMetric={["dsp_sales", "dsp_ecpm", "dsp_impressions", "dsp_total_purchases", "dsp_clicks"]}
            data={lineItemOverviewData}
            metrics={overviewMetrics}
            group={group}
            loading={isLoading}
            showLoadingSpinner={true}
            showMetricsSelector={true}
            context={"dspCampaign"}
            byline={
              <>
                {start.format("MMMM Do YYYY")}
                {" - "}
                {end.format("MMMM Do YYYY")}
              </>
            }
            showSkeletonLoadingEffect={true}
          >
            {/* Metadata Stat Tiles */}
            <Row className="pt-5">
              <Col xs={12} md={6} lg={2} className={`mb-4 px-2`}>
                <DetailedMetric
                  center
                  size={"small"}
                  id={"state"}
                  name={"State"}
                  value={secondaryMetricTileData?.state}
                />
              </Col>
              <Col xs={12} md={6} lg={2} className={`mb-4 px-2`}>
                <DetailedMetric
                  center
                  size={"small"}
                  id={"deliveryStatus"}
                  name={"Delivery Status"}
                  value={secondaryMetricTileData?.deliveryStatus}
                />
              </Col>
              <Col xs={12} md={6} lg={2} className={`mb-4 px-2`}>
                <DetailedMetric
                  center
                  size={"small"}
                  id={"lineItemStartDate"}
                  name={"Start Date"}
                  value={secondaryMetricTileData?.lineItemStartDate}
                />
              </Col>

              <Col xs={12} md={6} lg={2} className={`mb-4 px-2`}>
                <DetailedMetric
                  center
                  size={"small"}
                  id={"lineItemEndDate"}
                  name={"End Date"}
                  value={secondaryMetricTileData?.lineItemEndDate}
                />
              </Col>
              <Col xs={12} md={6} lg={2} className={`mb-4 px-2`}>
                <DetailedMetric
                  center
                  size={"small"}
                  id={"orderBudget"}
                  name={"Budget"}
                  formatter={(v) => formatCurrency(v, marketPlace.marketPlace, true)}
                  value={secondaryMetricTileData?.budget}
                />
              </Col>

              <Col xs={12} md={6} lg={2} className={`mb-4 px-2`}>
                <DetailedMetric
                  center
                  size={"small"}
                  id={"orderOptimization"}
                  name={"Optimization"}
                  value={secondaryMetricTileData?.optimization}
                />
              </Col>
            </Row>
            <Tabs selectedTab={selectedTab} tabs={DSP_TABS} onTabChange={setSelectedTab}>
              {isLoading ?
                <Col xs={12}>
                  <Loading height="300px" />
                </Col>
              : renderTabContent(selectedTab)}
            </Tabs>
          </MetricsCard>
        </Col>
      </Row>
    </div>
  );
};

const DSPLineItemDetails = () => {
  return (
    <DatesProvider>
      <Dashboard />
    </DatesProvider>
  );
};

export default DSPLineItemDetails;
